// Spec: specs/stats/sidebar/filters_tab/respondent_uids/functional_requirements.md

import React from 'react'
import { render, unmountComponentAtNode } from 'react-dom'
import { act } from 'react-dom/test-utils'

const copyMock = jest.fn()

// spelled out rather than imported, so a change to the shared clause has to be made here too
const STATUS = " and status in 'completed', 'excessive', 'dc-auto-rejected'"

jest.mock('@aytm/lib/common/copy-to-clipboard', () => ({
  __esModule: true,
  default: text => copyMock(text)
}))

// PrimeReact Button is a styled wrapper; a plain button keeps the assertions on label text.
jest.mock('primereact/button', () => ({
  __esModule: true,
  Button: ({ label, onClick }) => (
    <button type="button" onClick={onClick}>
      {label}
    </button>
  )
}))

import FiltersUids from './FiltersUids'

const DISPLAY_LIMIT = 50
const uidList = count => Array.from({ length: count }, (_, i) => i + 1)

let container = null

beforeEach(() => {
  container = document.createElement('div')
  document.body.appendChild(container)
  copyMock.mockReset()
  copyMock.mockResolvedValue(true)
})

afterEach(() => {
  unmountComponentAtNode(container)
  container.remove()
  container = null
})

const mount = props => {
  act(() => {
    render(<FiltersUids uids={[]} uidsTruncated={false} {...props} />, container)
  })
}

const toggle = () => {
  act(() => {
    container.querySelector('.uids-link').dispatchEvent(new MouseEvent('click', { bubbles: true }))
  })
}

const clickCopy = async () => {
  await act(async () => {
    container.querySelector('button').dispatchEvent(new MouseEvent('click', { bubbles: true }))
  })
}

const textarea = () => container.querySelector('.uids-textarea')
const copyButton = () => container.querySelector('button')
const notice = () => container.querySelector('.uids-notice')

describe('FiltersUids', () => {
  it('renders only the toggle until it is opened', () => {
    mount({ uids: uidList(3) })

    expect(container.querySelector('.uids-link').textContent).toBe('Show UUIDs')
    expect(textarea()).toBeNull()
    expect(copyButton()).toBeNull()
    expect(notice()).toBeNull()
  })

  it('opens and closes on the toggle', () => {
    mount({ uids: uidList(3) })

    toggle()
    expect(container.querySelector('.uids-link').textContent).toBe('Hide UUIDs')
    expect(textarea()).not.toBeNull()

    toggle()
    expect(container.querySelector('.uids-link').textContent).toBe('Show UUIDs')
    expect(textarea()).toBeNull()
  })

  describe('at or below the display limit', () => {
    it('shows the uid-pinned filter expression and offers Copy', () => {
      mount({ uids: uidList(3) })
      toggle()

      expect(textarea().value).toBe(`(((uid in 1,2,3${STATUS})))`)
      expect(copyButton().textContent).toBe('Copy 3 UUIDs as a filter')
    })

    it('uses the single-match form for exactly one respondent', () => {
      mount({ uids: [7] })
      toggle()

      expect(textarea().value).toBe(`(((uid = 7${STATUS})))`)
      expect(copyButton().textContent).toBe('Copy 1 UUID as a filter')
    })

    it('populates the text box at the limit itself', () => {
      mount({ uids: uidList(DISPLAY_LIMIT) })
      toggle()

      expect(textarea()).not.toBeNull()
      expect(copyButton().textContent).toBe(`Copy ${DISPLAY_LIMIT} UUIDs as a filter`)
    })
  })

  describe('above the display limit, at or below the pin ceiling', () => {
    it('offers Copy alone — the expression is too long to display', () => {
      mount({ uids: uidList(DISPLAY_LIMIT + 1) })
      toggle()

      expect(textarea()).toBeNull()
      expect(copyButton().textContent).toBe(`Copy ${DISPLAY_LIMIT + 1} UUIDs as a filter`)
    })

    it('copies every matching uid, not just the displayable ones', async () => {
      const uids = uidList(300)
      mount({ uids })
      toggle()
      await clickCopy()

      expect(copyMock).toHaveBeenCalledWith(`(((uid in ${uids.join(',')}${STATUS})))`)
    })
  })

  describe('above the pin ceiling', () => {
    it('refuses to pin and offers nothing to copy', () => {
      mount({ uids: uidList(DISPLAY_LIMIT), uidsTruncated: true })
      toggle()

      expect(notice().textContent).toBe('Too many respondents to pin. Narrow the filter and try again.')
      expect(textarea()).toBeNull()
      expect(copyButton()).toBeNull()
    })
  })

  it('shows an empty state when nothing matches', () => {
    mount({ uids: [] })
    toggle()

    expect(notice().textContent).toBe('No matching respondents.')
    expect(copyButton()).toBeNull()
  })

  describe('copying', () => {
    it('never writes to the clipboard on open — only on an explicit click', async () => {
      mount({ uids: uidList(3) })
      toggle()
      expect(copyMock).not.toHaveBeenCalled()

      await clickCopy()
      expect(copyMock).toHaveBeenCalledTimes(1)
    })

    it('confirms a successful write', async () => {
      mount({ uids: uidList(3) })
      toggle()
      await clickCopy()

      expect(copyMock).toHaveBeenCalledWith(`(((uid in 1,2,3${STATUS})))`)
      expect(copyButton().textContent).toBe('Copied!')
      expect(notice()).toBeNull()
    })

    it('does not confirm a failed write, and reveals the expression to copy by hand', async () => {
      copyMock.mockResolvedValue(false)
      mount({ uids: uidList(300) })
      toggle()
      await clickCopy()

      expect(copyButton().textContent).not.toBe('Copied!')
      expect(notice().textContent).toBe("Couldn't reach your clipboard. Select the text above and copy it by hand.")
      expect(textarea()).not.toBeNull()
    })

    it('reverts the confirmation to the copy label after the hold', async () => {
      jest.useFakeTimers()
      try {
        mount({ uids: uidList(3) })
        toggle()
        await clickCopy()
        expect(copyButton().textContent).toBe('Copied!')

        act(() => {
          jest.advanceTimersByTime(2000)
        })

        expect(copyButton().textContent).toBe('Copy 3 UUIDs as a filter')
      } finally {
        jest.useRealTimers()
      }
    })

    it('clears the failure notice once a later copy succeeds', async () => {
      copyMock.mockResolvedValueOnce(false)
      mount({ uids: uidList(300) })
      toggle()
      await clickCopy()
      expect(notice()).not.toBeNull()

      await clickCopy()

      expect(notice()).toBeNull()
      expect(textarea()).toBeNull()
      expect(copyButton().textContent).toBe('Copied!')
    })

    it('drops the failure notice and the revealed expression when the view is reopened', async () => {
      copyMock.mockResolvedValue(false)
      mount({ uids: uidList(300) })
      toggle()
      await clickCopy()
      expect(notice()).not.toBeNull()
      expect(textarea()).not.toBeNull()

      toggle()
      toggle()

      expect(notice()).toBeNull()
      expect(textarea()).toBeNull()
    })

    it('does not update state when a copy resolves after the view is gone', async () => {
      let resolveCopy
      copyMock.mockReturnValue(
        new Promise(resolve => {
          resolveCopy = resolve
        })
      )
      const consoleError = jest.spyOn(console, 'error').mockImplementation(() => {})

      mount({ uids: uidList(3) })
      toggle()
      act(() => {
        copyButton().dispatchEvent(new MouseEvent('click', { bubbles: true }))
      })

      act(() => {
        unmountComponentAtNode(container)
      })
      await act(async () => {
        resolveCopy(true)
      })

      expect(consoleError).not.toHaveBeenCalled()
      consoleError.mockRestore()
    })
  })
})
